import React, { useEffect } from "react";
import { useLazySearchProductsQuery } from "../../Services/ProductService";
import { Link } from "react-router-dom";
import Utils from "../../Utils/CommonProgram";
import { FaStar } from "react-icons/fa";

interface DealProps {
  title: string;
  categoryIds: string[];
  _id?: string; // Product ID
}

const DealsSection: React.FC<DealProps> = ({ title, categoryIds, _id }) => {
  const [searchProductsTrigger, { data }] = useLazySearchProductsQuery();

  useEffect(() => {
    if (categoryIds && categoryIds?.length > 0) {
      if (Array.isArray(categoryIds)) {
        if (typeof categoryIds[0] === "string") {
          searchProductsTrigger(`?category=${categoryIds.join(",")}`);
        } else if (
          typeof categoryIds[0] === "object" &&
          categoryIds[0] !== null
        ) {
          searchProductsTrigger(
            `?category=${categoryIds.map((cat: any) => cat?._id).join(",")}`,
          );
        } else {
          console.log("Unknown element type in array");
        }
      }
    }
  }, [categoryIds]);

  const filteredProducts = (data?.products || []).filter(
    (item: any) => item?._id !== _id,
  );
  const itemCount = Math.min(filteredProducts.length, 4);
  const gridColsClass =
    itemCount <= 1
      ? "grid-cols-1 max-w-[300px]"
      : itemCount === 2
        ? "grid-cols-2 max-w-[600px]"
        : itemCount === 3
          ? "grid-cols-3 max-w-[900px]"
          : "grid-cols-4";

  return (
    <div
      className={`bg-light-foreground dark:bg-dark-foreground md:px-16 px-6 transition-colors ${data && data?.products && data?.products?.length > 0 ? "py-8" : "py-0"}`}
    >
      {data && data?.products && data?.products?.length > 0 ? (
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {title || "Where to Find the Deals"}
          </h2>
        </div>
      ) : null}

      {/* Mobile & Tablet View (Scrollable) */}
      <div className="lg:hidden">
        <div
          className="flex overflow-x-auto scrollbar-hide -mx-6 px-6"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div className="flex gap-3 lg:gap-4 pb-4">
            {data &&
              data?.products &&
              data?.products?.length > 0 &&
              data?.products?.map((item: any, index) => {
                if (item?._id !== _id) {
                  return (
                    <Link
                      key={index}
                      to={`/gift-cards/product-details/${item?._id}/${item?.productName}`}
                      replace
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="flex-shrink-0 w-[180px] group bg-white dark:bg-dark-background rounded-2xl overflow-hidden cursor-pointer border border-gray-100 dark:border-white/[0.06] hover:border-gray-200 dark:hover:border-white/10 shadow-sm hover:shadow-md dark:shadow-none transition-all duration-300 active:scale-[0.98]"
                    >
                      <div className="p-3">
                        <div className="w-full mb-3 h-[143px] relative rounded-xl overflow-hidden flex items-center justify-center">
                          <div className="flex flex-row items-center gap-1.5 absolute top-2 left-2 z-10 flex-wrap">
                            {item.isFeatured && (
                              <span className="bg-light-primary-500 text-[10px] py-1 px-2.5 text-white rounded-full font-bold shadow-sm">
                                Featured
                              </span>
                            )}
                            {item.isPaysafe && (
                              <span className="bg-orange-500 text-[10px] py-1 px-2.5 text-white rounded-full font-bold shadow-sm">
                                Paysafe
                              </span>
                            )}
                          </div>
                          <img
                            src={item.productImage}
                            alt={item.productName}
                            className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <div className="w-full flex items-center justify-between text-sm">
                            <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white w-[80%] overflow-hidden text-ellipsis whitespace-nowrap">
                              {item.productName}
                            </h2>
                            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                              <span className="font-semibold text-sm">
                                {item.ratingValue
                                  ? Number.isInteger(item.ratingValue)
                                    ? item.ratingValue
                                    : item.ratingValue.toFixed(1)
                                  : "0"}
                              </span>
                              <FaStar className="w-3.5 h-3.5 text-yellow-500" />
                            </div>
                          </div>
                          <span className="text-sm text-gray-500 font-medium dark:text-gray-400 block">
                            {Utils.getItemPriceRange(item)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                }
              })}
          </div>
        </div>
      </div>

      {/* Desktop View (Grid) */}
      <div className={`hidden lg:grid ${gridColsClass} gap-6`}>
        {filteredProducts.map((item: any, index) => {
          return (
            <Link
              key={index}
              to={`/gift-cards/product-details/${item?._id}/${item?.productName}`}
              replace
              className="group bg-white dark:bg-dark-background rounded-2xl overflow-hidden cursor-pointer border border-gray-100 dark:border-white/[0.06] hover:border-gray-200 dark:hover:border-white/10 shadow-sm hover:shadow-md dark:shadow-none transition-all duration-300 hover:-translate-y-0.5"
              onClick={() => {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }}
            >
              <div className="p-3">
                <div className="w-full mb-3 h-[143px] relative rounded-xl overflow-hidden flex items-center justify-center">
                  <div className="flex flex-row items-center gap-1.5 absolute top-2 left-2 z-10 flex-wrap">
                    {item.isFeatured && (
                      <span className="bg-light-primary-500 text-[10px] py-1 px-2.5 text-white rounded-full font-bold shadow-sm">
                        Featured
                      </span>
                    )}
                    {item.isPaysafe && (
                      <span className="bg-orange-500 text-[10px] py-1 px-2.5 text-white rounded-full font-bold shadow-sm">
                        Paysafe
                      </span>
                    )}
                    {item.cashBack ? (
                      <span className="bg-yellow-400 text-[10px] py-1 px-2.5 text-yellow-900 rounded-full font-bold flex flex-row items-center shadow-sm">
                        <img
                          src={`${process.env.PUBLIC_URL}/icons/cashback.svg`}
                          alt="deals-sections"
                        />
                        &nbsp;{item.cashBack}%
                      </span>
                    ) : null}
                  </div>
                  <img
                    src={item.productImage}
                    alt={item.productName}
                    className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="w-full flex items-center justify-between text-sm">
                    <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white w-[80%] overflow-hidden text-ellipsis whitespace-nowrap">
                      {item.productName}
                    </h2>
                    <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                      <span className="font-semibold text-sm">
                        {item.ratingValue
                          ? Number.isInteger(item.ratingValue)
                            ? item.ratingValue
                            : item.ratingValue.toFixed(1)
                          : "0"}
                      </span>
                      <FaStar className="w-3.5 h-3.5 text-yellow-500" />
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 font-medium dark:text-gray-400 block">
                    {Utils.getItemPriceRange(item)}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default DealsSection;
